import type { FC } from 'react';
import { useState, useEffect, useCallback } from 'react';
import { Button } from '@flowus/common/components/button';
import { request } from 'src/common/request';
import { message } from '@flowus/common/components/message';
import { useAppTranslation } from '@flowus/common/i18n/use-translation';
// 使用正确的导入路径
import type { BlockType, BotDTO, IntegrationDetailResponse } from '@next-space/fe-api-idl';
import { PermissionRole } from '@next-space/fe-api-idl';
import { useCurrentSpace } from 'src/hooks/space';
import { useTocIds } from '../main/aside/toc/use-toc-id';
import { useCurrentUser } from 'src/hooks/user';
import { getPermissions } from 'src/hooks/share/use-permissions';
import { cache } from 'src/redux/store';
import { isLinkPageBlock, isPageLike } from 'src/utils/block-type-utils';
import { TocItem } from '../main/aside/toc/toc-item';
import { TocListContext } from 'src/components/move-to/toc-list';
import { ReadonlyContext } from 'src/hooks/block/use-block-locked';
import type { SearchItem } from 'src/components/search';
import { SelectPageContext } from './select-page-context';
import { Search } from './search';
import { buildAuthorizationCodeRequest } from './oauth-pkce';

interface PageInfo {
  id: string;
  title: string;
  icon?: any;
  type?: BlockType;
  data?: {
    segments?: any;
  };
}

interface SelectSpacePageProps {
  integration?: IntegrationDetailResponse;
  scope: string;
  redirectUri: string;
  state: string;
  codeChallenge?: string;
  codeChallengeMethod?: string;
  onBack: () => void;
  bot?: BotDTO;
  onUpdate?: (pageIds: string[]) => void;
  selectedTeamPages?: Record<string, boolean>;
  selectedPersonPages?: Record<string, boolean>;
  selectedManualPages?: Record<string, PageInfo>;
}

export const SelectSpacePage: FC<SelectSpacePageProps> = ({
  integration,
  scope,
  redirectUri,
  state,
  codeChallenge,
  codeChallengeMethod,
  onBack,
  bot,
  selectedTeamPages: _selectedTeamPages,
  selectedPersonPages: _selectedPersonPages,
  selectedManualPages: _selectedManualPages,
  onUpdate,
}) => {
  const { t } = useAppTranslation();
  // 使用钩子获取用户所有的空间列表
  const currentUser = useCurrentUser();

  const currentSpace = useCurrentSpace();
  const [selectedTeamPages, setSelectedTeamPages] = useState<Record<string, boolean>>(
    _selectedTeamPages ?? {}
  );
  const [selectedPersonPages, setSelectedPersonPages] = useState<Record<string, boolean>>(
    _selectedPersonPages ?? {}
  );
  const [selectedManualPages, setSelectedManualPages] = useState<Record<string, PageInfo | null>>(
    _selectedManualPages ?? {}
  );
  const [loading, setLoading] = useState(false);
  const tocIds = useTocIds(bot?.spaceId ?? currentSpace.uuid, currentUser.uuid, false);

  const teamPages = tocIds.team.filter((v) => {
    const block = cache.blocks[v];
    if (isLinkPageBlock(block)) {
      return false;
    }
    const { role } = getPermissions(v);
    return role === PermissionRole.EDITOR || role === PermissionRole.WRITER;
  });
  const personPages = tocIds.person.filter((v) => {
    const block = cache.blocks[v];
    if (!block) return false;
    if (!isPageLike(block.type)) {
      return false;
    }
    if (isLinkPageBlock(block)) {
      return false;
    }
    const { role } = getPermissions(v);
    return role === PermissionRole.EDITOR || role === PermissionRole.WRITER;
  });
  const manualPages = Object.entries(selectedManualPages ?? {}).map((v) => v[0]);

  // 处理全选
  const handleSelectAll = (type: 'team' | 'person') => {
    if (type === 'team') {
      if (Object.keys(selectedTeamPages).length > 0) {
        setSelectedTeamPages({});
        return;
      }

      setSelectedTeamPages(
        teamPages.reduce((acc, id) => {
          acc[id] = true;
          return acc;
        }, {} as Record<string, boolean>)
      );
    } else {
      if (Object.keys(selectedPersonPages).length > 0) {
        setSelectedPersonPages({});
        return;
      }

      setSelectedPersonPages(
        personPages.reduce((acc, id) => {
          acc[id] = true;
          return acc;
        }, {} as Record<string, boolean>)
      );
    }
  };

  // 处理授权确认
  const handleConfirm = async () => {
    // 合并三种页面的选择：team、person和手动选择的页面
    const combinedSelectedPages = {
      ...selectedTeamPages,
      ...selectedPersonPages,
    };

    // 添加手动选择的页面
    Object.keys(selectedManualPages).forEach((pageId) => {
      combinedSelectedPages[pageId] = true;
    });

    if (Object.keys(combinedSelectedPages).length === 0) {
      message.warning(t('components.oauth.select_space_page.select_at_least_one'));
      return;
    }
    const blockIdsForBot = Object.entries(combinedSelectedPages)
      .map(([k, v]) => {
        if (v) {
          return k;
        }
      })
      .filter((v) => v) as string[];

    setLoading(true);
    try {
      const requestBody = buildAuthorizationCodeRequest({
        integrationId: integration?.id || '',
        spaceId: currentSpace.uuid,
        blockIdsForBot: blockIdsForBot ?? [],
        redirectUri,
        scope,
        state,
        codeChallenge,
        codeChallengeMethod,
      });
      const res = await (request.integration.createAuthorizationCode as any)(requestBody);
      const callbackUrl = `${redirectUri}?code=${res.authorizationCode}${
        state ? `&state=${state}` : ''
      }${codeChallenge ? `&code_challenge=${codeChallenge}` : ''}`;
      window.location.href = callbackUrl;
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  const selectPage = useCallback(
    (pageInfo: SearchItem) => {
      const pageId = pageInfo.uuid;

      // 判断是team还是person页面
      const isTeamPage = teamPages.includes(pageId);
      const isPersonPage = personPages.includes(pageId);

      if (isTeamPage) {
        setSelectedTeamPages((prev) => {
          const newState = { ...prev };
          newState[pageId] = !newState[pageId];
          return newState;
        });
      } else if (isPersonPage) {
        setSelectedPersonPages((prev) => {
          const newState = { ...prev };
          newState[pageId] = !newState[pageId];
          return newState;
        });
      } else {
        // 不在team或person页面中的，作为手动选择页面处理
        setSelectedManualPages((prev) => {
          const newState = { ...prev };
          if (newState[pageId]) {
            // 如果已选中，则移除
            newState[pageId] = null;
          } else {
            // 如果未选中，则添加
            newState[pageId] = {
              id: pageId,
              title: pageInfo.title,
              icon: pageInfo.data?.icon,
              type: pageInfo.type,
              data: pageInfo.data,
            };
          }
          return newState;
        });
      }
    },
    [teamPages, personPages]
  );

  useEffect(() => {
    _selectedTeamPages && setSelectedTeamPages(_selectedTeamPages);
    _selectedPersonPages && setSelectedPersonPages(_selectedPersonPages);
    _selectedManualPages && setSelectedManualPages(_selectedManualPages);
  }, [_selectedTeamPages, _selectedPersonPages, _selectedManualPages]);
  if (!integration) return null;

  return (
    <>
      <h1 className="mt-4 mx-2.5 text-center w-full text-h4 font-medium">
        {t('components.oauth.select_space_page.allow_access', { name: integration.name })}
      </h1>
      <TocListContext.Provider value={selectPage}>
        <Search
          selectedTeamPages={selectedTeamPages}
          selectedPersonPages={selectedPersonPages}
          selectedManualPages={selectedManualPages}
        />
      </TocListContext.Provider>
      <div className="mt-4 text-t3 text-grey2 mb-1">{t('common.select_pages')}</div>
      <TocListContext.Provider value={selectPage}>
        <SelectPageContext.Provider value={true}>
          <ReadonlyContext.Provider value={true}>
            <div className="h-[50vh] pt-0 space-y-1 overflow-y-auto mb-1 pr-1">
              {/* 手动选择部分 */}
              {manualPages.length > 0 && (
                <TocGroup title={t('common.manual_selection')}>
                  {manualPages.map((id) => (
                    <TocItem key={id} uuid={id} deep={0} selected={!!selectedManualPages[id]} />
                  ))}
                </TocGroup>
              )}

              <TocGroup title={t('common.space')}>
                {teamPages.length > 0 ? (
                  <div className="mt-1 w-full flex justify-between px-2">
                    <div className="text-t4-medium text-grey3">{t('common.top_level_pages')}</div>
                    <div
                      className="text-t4-medium text-active_color cursor-pointer animate-hover p-1"
                      onClick={() => handleSelectAll('team')}
                    >
                      {Object.keys(selectedTeamPages).length === 0
                        ? t('common.select_all')
                        : t('common.deselect_all')}
                    </div>
                  </div>
                ) : (
                  <div className="text-t4-medium text-grey3 p-1">{t('common.no_pages')}</div>
                )}
                {teamPages.map((id) => (
                  <TocItem key={id} uuid={id} deep={0} selected={selectedTeamPages[id]} />
                ))}
              </TocGroup>
              <TocGroup title={t('common.private')}>
                {personPages.length > 0 ? (
                  <div className="mt-1 w-full flex justify-between px-2">
                    <div className="text-t4-medium text-grey3">{t('common.top_level_pages')}</div>
                    <div
                      className="text-t4-medium text-active_color cursor-pointer animate-hover p-1"
                      onClick={() => handleSelectAll('person')}
                    >
                      {Object.keys(selectedPersonPages).length === 0
                        ? t('common.select_all')
                        : t('common.deselect_all')}
                    </div>
                  </div>
                ) : (
                  <div className="text-t4-medium text-grey3 p-1">{t('common.no_pages')}</div>
                )}
                {personPages.map((id) => (
                  <TocItem key={id} uuid={id} deep={0} selected={selectedPersonPages[id]} />
                ))}
              </TocGroup>
            </div>
            <div className="w-full flex space-x-5 px-1 mb-1">
              <Button
                className="bg-white1 text-black flex-1 border border-grey4 h-9"
                onClick={onBack}
              >
                {t('common.back')}
              </Button>
              <Button
                className="flex-1 border border-grey4 h-9"
                onClick={handleConfirm}
                colorType="primary"
              >
                {redirectUri
                  ? t('components.oauth.select_space_page.allow_access_button')
                  : t('components.oauth.select_space_page.update_permission')}
              </Button>
            </div>
          </ReadonlyContext.Provider>
        </SelectPageContext.Provider>
      </TocListContext.Provider>
    </>
  );
};

interface TocGroupProps {
  title: string;
  children: React.ReactNode;
}
export const TocGroup: FC<TocGroupProps> = (props) => {
  const [hideGroup, setHideGroup] = useState(false);
  return (
    <div>
      <div
        className="w-full py-1.5 text-t4-medium text-grey3 px-2 rounded cursor-pointer bg-grey7 hover:bg-grey6"
        onClick={() => {
          setHideGroup(!hideGroup);
        }}
      >
        {props.title}
      </div>
      <div
        style={{
          display: hideGroup ? 'none' : '',
        }}
      >
        {props.children}
      </div>
    </div>
  );
};
